import * as React from 'react';
import PageModel from '@app/Model/PageModel';
import { Card, CardTitle, CardBody, Grid, GridItem, PageSection, Title } from '@patternfly/react-core';
import { Link } from 'react-router-dom';
import { UserContext } from '@app/Context/UserContext';
import ChartDataHelper from '@app/Service/ChartDataHelper';
import ChartAverageInterface from '@app/Interface/Chart/ChartAverageInterface';
import ChartMetricInterface from '@app/Interface/Chart/ChartMetricInterface';
import ItemLoader from '@app/Components/ItemLoader';
import NDALineChart from '@app/Components/Chart/NDALineChart';
import ChartHistoryTable from '@app/Components/Chart/HistoryTable';
import ChartMetricsTable from '@app/Components/Chart/MetricsTable';
import UserChartNotificationSettingsInterface from '@app/Interface/UserChartNotificationSettingsInterface';
import UserAuthentication from '@app/Security/UserAuthentication';
import TemplateDefinitions from '@app/utils/TemplateDefinitions';
import UserInterface from '@app/Interface/UserInterface';
import htmlParser from '@app/utils/htmlParser';
import { PageRouteContext } from '@app/Context/PageRouteContext';
import PageRouteHelper from "@app/Service/PageRouteHelper";

interface ChartPageProps {
    pageData: PageModel,
}

const userHasAccessToNotificationsPage = (user: UserInterface) : boolean => {
    let canView = false;

    // check system roles first
    if (user.viewPermissions.by_system.posts && user.viewPermissions.by_system.posts.length > 0) {
        canView = UserAuthentication.canUserViewTemplate(
            user.viewPermissions.by_system.posts,
            TemplateDefinitions.TEMPLATE_NOTIFICATIONS_PAGE
        );
    }

    // only check fpu roles if we haven't found the page yet
    if (!canView && user.viewPermissions.by_fpu) {
        const matchingFPU = user.viewPermissions.by_fpu.find(fpuPermission => Number(fpuPermission.fpu_id) === user.currentFpuId);

        if (matchingFPU) {
            canView = UserAuthentication.canUserViewTemplate(
                matchingFPU.posts,
                TemplateDefinitions.TEMPLATE_NOTIFICATIONS_PAGE
            );
        }
    }

    return canView;
};

const getUserChartNotificationThreshold = (user: UserInterface, chartType: string) => {
    // Check if the user has a notification threshold set for this chart
    let userNotificationThreshold: UserChartNotificationSettingsInterface|undefined;
    if (typeof user.fpuChartNotificationSettings !== 'boolean') {
        userNotificationThreshold = user.fpuChartNotificationSettings.find(
            setting => setting.fpu === user.currentFpuId && setting.chart === chartType
        )
    }

    return userNotificationThreshold;
};

const ChartPage: React.FC<ChartPageProps> = ({ pageData }) => {
    const { userState } = React.useContext(UserContext);

    const { pageRouteState } = React.useContext(PageRouteContext);
    const notificationsPageRoute = PageRouteHelper.getPageRouteByTemplate(pageRouteState.pageRoutes, TemplateDefinitions.TEMPLATE_NOTIFICATIONS_PAGE);
    
    const chartType = pageData.acf.chart_type ?? '';

    const [chartAveragesData, setChartAveragesData] = React.useState<ChartAverageInterface[]>([]);
    const [chartMetricsData, setChartMetricsData] = React.useState<ChartMetricInterface|null>(null);
    const [loading, setLoading] = React.useState(true);
    const [userHasNotificationsAccess, setUserHasNotificationsAccess] = React.useState(
        userHasAccessToNotificationsPage(userState.user)
    );
    const [userNotificationThreshold, setUserNotificationThreshold] = React.useState(
        getUserChartNotificationThreshold(userState.user, chartType)
    );
    
    
    const hasNotificationThreshold = !!userNotificationThreshold && userNotificationThreshold.active;

    // When the page, user, or FPU change, fetch new chart data
    React.useEffect(() => {
        setLoading(true);
        Promise.all([
            ChartDataHelper.getChartAveragesData(chartType, userState.user.currentFpuId, pageData.id),
            ChartDataHelper.getChartMetricsData(chartType, userState.user.currentFpuId, pageData.id)
        ]).then(chartData => {
            setChartAveragesData(chartData[0]);
            setChartMetricsData(chartData[1]);
        }).catch(() => {
            setChartAveragesData([]);
            setChartMetricsData(null);
        }).finally(() => {
            setUserHasNotificationsAccess(userHasAccessToNotificationsPage(userState.user));
            setUserNotificationThreshold(getUserChartNotificationThreshold(userState.user, chartType));
            setLoading(false);
        })
        
    }, [pageData.id, userState.user.id, userState.user.currentFpuId, chartType, userState.user]);

    const getChartContent = () => {
        if (loading) {
            return <ItemLoader />;
        }

        if (chartAveragesData.length === 0 || chartMetricsData === null) {
            return <p><em>No chart data to show for the selected producer</em></p>;
        }

        return (
            <Grid hasGutter>
                <GridItem lg={8} md={12}>
                    <Card>
                        <CardTitle>
                            Historical Trend {' '}
                            {userHasNotificationsAccess && notificationsPageRoute &&
                                <Link to={notificationsPageRoute.path}>
                                    {hasNotificationThreshold ? 'Edit' : 'Add a'} notification
                                </Link>
                            }
                        </CardTitle>
                        <CardBody>
                            <NDALineChart 
                                // Chart data needs to be ordered from old to new
                                data={[...chartAveragesData].reverse()} 
                                chartType={chartType}
                                hasBaseline={!!pageData.acf.chart_baseline && pageData.acf.chart_baseline !== 0}
                                hasNotificationThreshold={hasNotificationThreshold}
                                userNotificationThreshold={userNotificationThreshold}
                                baselineCaption={pageData.acf.chart_baseline_caption ?? ''} 
                            />
                        </CardBody>
                    </Card>
                    <Card>
                        <CardTitle>Metrics</CardTitle>
                        <CardBody>
                            <ChartMetricsTable data={chartMetricsData} />
                        </CardBody>
                    </Card>
                </GridItem>
                <GridItem lg={4} md={12}>
                    <Card>
                        <CardTitle>History</CardTitle>
                        <CardBody>
                            <ChartHistoryTable 
                                downloadFilename={`${chartType}-chart-history-export.csv`} 
                                data={chartAveragesData} 
                            />
                        </CardBody>
                    </Card>
                </GridItem>
            </Grid>
        )
    };

    return (
        <>
            <PageSection>
                <Title headingLevel="h1" className="pf-u-mb-md">{pageData.title}</Title>
                <div className="pf-c-content pf-u-mb-lg">
                    {htmlParser.htmlToJSX(pageData.content ?? '')}
                </div>
                
                {getChartContent()}

                {pageData.acf.footer &&
                    <div className="pf-c-content pf-u-mt-lg">
                        {htmlParser.htmlToJSX(pageData.acf.footer ?? '')}
                    </div>
                }
            </PageSection>
        </>
    )

};

export default ChartPage;